// Duplicate shows (2026-10-05). The backend decides: a show with crew or money attached
// answers 409 with the list of blockers. These helpers only call it and surface the result.
import { apiRequest } from "../../services/apiService";
import { alertDialogAciton } from "./auth";

export interface DuplicatePreview {
  eventId: string;
  eventName: string | null;
  isDuplicate: boolean;
  canMark: boolean;
  blockers: string[];
  warnings: string[];
}

const bulletList = (items: string[]) => items.map((i) => `• ${i}`).join("\n");

export const fetchDuplicatePreview = async (
  eventId: string,
): Promise<{ data: DuplicatePreview | null; message: string | null }> => {
  const { response, error } = await apiRequest({
    method: "GET",
    url: `events/${eventId}/duplicate-preview`,
    isToken: true,
  });
  if (response?.status === 200) {
    // The API wraps service results in { success, status, data }.
    const body = (response as any)?.data;
    return { data: (body?.data ?? body) as DuplicatePreview, message: null };
  }
  return {
    data: null,
    message: (error as any)?.response?.data?.message || "Could not check this show.",
  };
};

const errorDialog = (dispatch: any, error: any, fallback: string) => {
  const body = error?.response?.data;
  const blockers: string[] = Array.isArray(body?.blockers) ? body.blockers : [];
  dispatch(
    alertDialogAciton({
      open: true,
      title: "Error",
      message: blockers.length
        ? `${body?.message || fallback}\n\n${bulletList(blockers)}`
        : body?.message || fallback,
    }, undefined),
  );
};

export const markDuplicateAction = ({
  eventId,
  keepEventId,
  successCallback,
}: {
  eventId: string;
  keepEventId?: string | null;
  successCallback?: () => void;
}) => {
  return async (dispatch: any) => {
    const { response, error } = await apiRequest({
      method: "PATCH",
      url: `events/${eventId}/mark-duplicate`,
      data: keepEventId ? { keepEventId } : {},
      isToken: true,
    });
    if (response?.status === 200) {
      successCallback?.();
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Success",
          message: "Show marked as duplicate. It is hidden now; turn off 'Hide duplicates' on the dashboard to see it.",
        }, undefined),
      );
    } else {
      errorDialog(dispatch, error, "Could not mark this show as duplicate.");
    }
  };
};

export const unmarkDuplicateAction = ({
  eventId,
  successCallback,
}: {
  eventId: string;
  successCallback?: () => void;
}) => {
  return async (dispatch: any) => {
    const { response, error } = await apiRequest({
      method: "PATCH",
      url: `events/${eventId}/unmark-duplicate`,
      isToken: true,
    });
    if (response?.status === 200) {
      successCallback?.();
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Success",
          message: "Show is no longer marked as duplicate.",
        }, undefined),
      );
    } else {
      errorDialog(dispatch, error, "Could not unmark this show.");
    }
  };
};

/** Confirm dialog text built from the server preview. */
export const duplicateConfirmMessage = (p: DuplicatePreview) => {
  const lines = [
    `Mark "${p.eventName || "this show"}" as a duplicate?`,
    "",
    "It will be hidden from the dashboard, the reports and the automatic emails. Nothing is deleted: turn off 'Hide duplicates' on the dashboard to see it and unmark it later.",
  ];
  if (p.warnings.length) lines.push("", "Heads up:", bulletList(p.warnings));
  return lines.join("\n");
};

export const duplicateBlockedMessage = (p: DuplicatePreview) =>
  [
    "This show has crew or money attached, so it can't be marked as a duplicate:",
    "",
    bulletList(p.blockers),
    "",
    "Cancel or archive it instead, or move the crew and documents to the copy you're keeping first.",
  ].join("\n");

// ── "Does this show already exist?" (Casper, 2026-10-05) ─────────────────────
export interface SimilarShow {
  _id: string;
  eventName: string | null;
  startDay: string | null;
  endDay: string | null;
  venue: string | null;
  venueCity: string | null;
  venueState: string | null;
  client: string | null;
  createdBy: string | null;
  lifecycle: string;
  cancelled: boolean;
  approved: boolean;
  isEstimateGenerated: boolean;
  contactInformationId: string | null;
  assignedCoordinatorId: string[];
  reasons: string[];
}

/**
 * Shows that look like the one about to be created. A warning only: on any error the
 * caller just goes on creating (never blocks the wizard).
 */
export const fetchSimilarShows = async (params: {
  eventName?: string;
  eventStart?: string;
  eventEnd?: string;
  userId?: string;
  venue?: string;
  excludeEventId?: string;
}): Promise<SimilarShow[]> => {
  if (!params?.eventName || !params?.eventStart) return [];
  try {
    const { response } = await apiRequest({
      method: "GET",
      url: "events/similar",
      params: Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== "")),
      isToken: true,
    });
    if (response?.status !== 200) return [];
    const body = (response as any)?.data;
    const shows = body?.data?.shows ?? body?.shows;
    return Array.isArray(shows) ? (shows as SimilarShow[]) : [];
  } catch {
    return [];
  }
};

/**
 * The "Mark as duplicate" / "Unmark duplicate" flow for show options, with restore
 * also available on duplicate dashboard rows: confirm, ask the server what blocks,
 * then mark or unmark. `onDone` runs after a successful change (refresh the view).
 */
export const toggleDuplicateFlow = async ({
  dispatch,
  eventId,
  isDuplicate,
  onDone,
}: {
  dispatch: any;
  eventId: string;
  isDuplicate: boolean;
  onDone?: () => void;
}) => {
  if (!eventId) return;
  if (isDuplicate) {
    dispatch(
      alertDialogAciton(
        {
          open: true,
          title: "Warning",
          message: "Unmark this show as duplicate? It goes back to the dashboard as a normal show.",
          okLabel: "Unmark Duplicate",
          onOk: () => dispatch(unmarkDuplicateAction({ eventId, successCallback: onDone })),
        },
        undefined,
      ),
    );
    return;
  }
  const { data: preview, message } = await fetchDuplicatePreview(eventId);
  if (!preview) {
    dispatch(alertDialogAciton({ open: true, title: "Error", message }, undefined));
    return;
  }
  if (!preview.canMark) {
    dispatch(
      alertDialogAciton(
        { open: true, title: "Can't mark as duplicate", message: duplicateBlockedMessage(preview) },
        undefined,
      ),
    );
    return;
  }
  dispatch(
    alertDialogAciton(
      {
        open: true,
        title: "Mark as duplicate",
        message: duplicateConfirmMessage(preview),
        okLabel: "Mark as Duplicate",
        onOk: () => dispatch(markDuplicateAction({ eventId, successCallback: onDone })),
      },
      undefined,
    ),
  );
};

/** "Marked as duplicate by Casper Gomez on Oct 5, 2026" (missing parts are skipped). */
export const duplicateMarkedLabel = (by?: string | null, at?: string | Date | null): string => {
  const d = at ? new Date(at) : null;
  const when =
    d && !isNaN(d.getTime())
      ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : null;
  return ["Marked as duplicate", by ? `by ${by}` : null, when ? `on ${when}` : null].filter(Boolean).join(" ");
};
